"use client"
import DateSelectorButton from "src/components/dateselector/DateSelectorButton";
import FilterSelectorButton from "src/components/filterselector/FilterSelectorButton";
import LocationSelectorButton from "src/components/locationselector/LocationSelectorButton";
import { format, isToday } from "date-fns";
import locale from "date-fns/locale/nb";
import { useLocationContext } from "src/lib/contexts/LocationContext";
import { useProgramDatesContext } from "src/lib/contexts/ProgramDatesWrapper";
import ProgramControlsSponsor from "src/components/takoversponsor/ProgramControlsSponsor";
import classNames from "classnames";
import { SanityReference } from "next-sanity";
import useElementInView from "src/lib/client/useElementInView";
import { useState, useEffect } from "react";
import useScrollToTopOnChange from "src/lib/client/useScrollToTopOnChange";
import styles from './ProgramControls.module.css';

//#region [Props]
type ProgramControlsProps = {
    hasSponsor?: boolean;
    sponsorClickUrl?: string | null;
    sponsorTopImageDesktop?: SanityReference[] | null;
    sponsorTopImageMobile?: SanityReference[] | null;
    hasSponsorBg?: boolean;
};
//#endregion

//#region [Component]
export default function ProgramControls({ hasSponsor, sponsorClickUrl, sponsorTopImageDesktop, sponsorTopImageMobile, hasSponsorBg }: ProgramControlsProps) {
    const locationContext = useLocationContext();
    const selectedDateContext = useProgramDatesContext();

    const date = selectedDateContext.selectedDate;
    const location = locationContext.location;

    useScrollToTopOnChange([date, location], { behavior: "smooth" });


    // https://css-tricks.com/how-to-detect-when-a-sticky-element-gets-pinned/
    const { ref, entry } = useElementInView<HTMLDivElement>({
        /* Optional options */
        threshold: 1,
        delay: 200
    });

    let headerText = "Kinoprogram";
    if (locationContext.location) {
        headerText += ` for ${locationContext.location[0].toUpperCase()}${locationContext.location.substring(1)}`;
    }

    if (selectedDateContext.selectedDate) {
        if (isToday(selectedDateContext.selectedDate)) {
            headerText += " i dag";
        } else {
            headerText += ` ${format(selectedDateContext.selectedDate, "iiii d. MMM", { locale })}`;
        }
    }

    // 👇 juster verdien til ca. høyden på den LAVE sticky-headeren (f.eks. 64–96px)
    const STICKY_BUFFER_PX = 80;

    // 1) Lag en EGEN sentinel-observasjon (ikke på headeren)

    const { ref: sentinelRef, entry: sentinelEntry } =
        useElementInView<HTMLDivElement>({
            threshold: 0,
            rootMargin: `-${STICKY_BUFFER_PX}px 0px 0px 0px`,
        });

    // const { ref: sentinelRef, entry: sentinelEntry } =
    //      useElementInView<HTMLDivElement>({ threshold: 0, rootMargin: "-1px 0px 0px 0px" })


    // 2) State for å vise i DevTools/Console
    const [isSticky, setIsSticky] = useState(false);

    // 3) Oppdater state når SENTINELEN går inn/ut av viewport
    useEffect(() => {
        const next = !(sentinelEntry?.isIntersecting ?? true);
        setIsSticky(next);
        //console.log("ProgramControls isSticky:", next);
    }, [sentinelEntry?.isIntersecting]);

    return (
        <>
            {/* 👇 1px sentinel i normal dokumentflyt */}
            <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />

            <div
                className={classNames(styles.controlsContainer, {
                    [styles.isSticky]: isSticky,
                    [styles.hasSponsorBg]: hasSponsorBg
                })}
            >
                {hasSponsor && (
                    <ProgramControlsSponsor
                        isSticky={isSticky}
                        className={classNames(styles.programControlsSponsor, {
                            [styles.isSticky]: isSticky
                        })}
                        sponsorClickUrl={sponsorClickUrl}
                        sponsorTopImageDesktop={sponsorTopImageDesktop}
                        sponsorTopImageMobile={sponsorTopImageMobile}
                    />
                )}
                <h1 className={classNames(styles.title, {
                    [styles.isSticky]: isSticky,
                    [styles.hasSponsor]: hasSponsor
                })}>
                    {headerText}
                </h1>
                <DateSelectorButton className={styles.dateSelectorButton} useStaticText />
                <LocationSelectorButton className={styles.locationSelectorButton} useStaticText />
                <FilterSelectorButton className={styles.filterSelectorButton} />
            </div>
        </>
    );
}
//#endregion


